import { useState } from "react";

import { Bento, StateMark } from "@/components/ui/box";
import { useTabDraft } from "@/components/ui/box-draft";
import type { Box, BoxState, Effect, FactBox, FieldAria, FieldSave, ListBox, Part, Read, Result, ResultLine, TaskBox } from "@/components/ui/box.schema";
import { Input } from "@/components/ui/input";
import { Register, RegisterRow } from "@/components/ui/register";
import { Textarea } from "@/components/ui/textarea";
import { COURSE_HUE } from "@/lib/course-signature";
import { cn } from "@/lib/utils";

import type { StatesGroup } from "./fixtures";
import { HomeView } from "./HomeView";

/**
 * A v2.2's States specimens (scenes/states.js), each its own one-box page so
 * every state is drawn, focused and judged alone. Every tenant draws them with
 * its own preset; the content is the package's, from TDAY-101 and USUL-210.
 */

const imam = { system: "imam" } as const;
// Instants, as areas pass them; the kit says them in the page's zone (CASE-039).
const ZONE = "America/New_York";
const classroom = { system: "classroom", readAt: "2026-10-20T22:26:00Z" } as const;
const AT = "2026-10-21T04:44:00Z";
const none: Result = { state: "none" };
const rank1 = { eligible: true, blocked: null, rank: 1 };
const back = "/design-system";
const noop = () => undefined;

function ready<T>(value: T): Read<T> {
  return { status: "ready", value, readAt: AT };
}

const is = (state: BoxState) => () => state;
const says = (text: string) => () => text;

interface Spec {
  /** The specimen's own page key and journey, the same in every group that draws it (P4). */
  key?: string;
  cap: string;
  /** Where the specimen comes from, set beside the caption. */
  note?: string;
  full?: boolean;
  open?: boolean;
  /** Builds the one box; it may hold its own presses' state. */
  useBox: () => Box;
}

/** The result of a press, held so the box draws it. */
function useResult(initial: Result = none) {
  const [result, setResult] = useState<Result>(initial);
  const settle = (next: Result) => async () => {
    setResult(next);
    return next;
  };
  return [result, settle] as const;
}

type Confirmed = Extract<Result, { state: "confirmed" }>;
const confirmed = (cap: string | undefined, effects: string[], remains: string[] = []): Confirmed => ({ state: "confirmed", ...(cap ? { cap } : {}), at: AT, effects, remains });

/* ---------- Fact ---------- */

const attendance = { label: "Attendance, Session 9", owner: imam, fold: "settled" } as const;

const fact: Spec[] = [
  {
    cap: "An answer in figures",
    useBox: (): FactBox<null> => ({
      kind: "fact", id: "attendance", job: "Say who came to Session 9", ...attendance, read: ready(null),
      detail: says("Roll recorded at close"),
      figures: () => [{ value: 9, unit: "present" }, { value: 0, unit: "absent" }],
      exits: [{ label: "All sessions", to: back }],
    }),
  },
  {
    cap: "An answer in words",
    useBox: (): FactBox<null> => ({
      kind: "fact", id: "get-ready", job: "Say what to do before the session", label: "Get ready", owner: imam, fold: "settled", read: ready(null),
      prose: says("Explain the competing goods in the case and support one provisional counsel."),
      detail: says("Before the session: review the graded case reflection."),
    }),
  },
  {
    cap: "A record several people edit says when it was read",
    useBox: function useSharedRecord(): FactBox<null> {
      // Read as the page opens, so the kit says "Today".
      const [readAt] = useState(() => new Date().toISOString());
      return {
        kind: "fact", id: "delivery", job: "Say how courses meet unless a course says otherwise", label: "Course delivery", owner: imam, fold: "settled", readTime: true,
        read: { status: "ready", value: null, readAt },
        prose: says("In person, with a Zoom room for learners who join from home"), detail: says("Every new course starts here"),
      };
    },
  },
  {
    cap: "Still reading: no number yet",
    useBox: (): FactBox<null> => ({
      kind: "fact", id: "attendance", job: "Say who came to Session 9", ...attendance, read: { status: "loading" },
      detail: says("Roll recorded at close"),
      figures: () => [{ value: 9, unit: "present" }],
    }),
  },
  {
    cap: "Couldn't read: says so, with Try again",
    useBox: function useFailed(): FactBox<null> {
      const read = useRecovering<null>({ status: "failed", sentence: "Couldn't read attendance", detail: "The roll may already be recorded. Try again in a moment.", retry: noop });
      return {
        kind: "fact", id: "attendance", job: "Say who came to Session 9", ...attendance, read,
        detail: says("Roll recorded at close"), figures: () => [{ value: 9, unit: "present" }, { value: 0, unit: "absent" }],
      };
    },
  },
  {
    cap: "Not refreshed: the last answer, with when it was read",
    useBox: function useStale(): FactBox<null> {
      const read = useRecovering<null>({ status: "stale", last: null, readAt: AT, sentence: "Couldn't refresh. A later change isn't shown.", refresh: noop });
      return {
        kind: "fact", id: "attendance", job: "Say who came to Session 9", ...attendance, read,
        detail: says("Roll recorded at close"), figures: () => [{ value: 9, unit: "present" }],
      };
    },
  },
  {
    cap: "Nothing there yet, said in words",
    useBox: (): FactBox<null> => ({
      kind: "fact", id: "recording", job: "Link the session's recording", label: "Recording", owner: imam, fold: "settled",
      read: { status: "empty", sentence: "No recording yet", sub: "A link appears here once the teacher adds one." },
      detail: says(""),
    }),
  },
  {
    cap: "Not read yet: no answer, and no zero",
    useBox: (): FactBox<null> => ({
      kind: "fact", id: "classroom", job: "Show what Classroom holds for this course", label: "Topics and materials", owner: { system: "classroom", readAt: null }, fold: "settled",
      read: { status: "unread", sentence: "Not read yet" },
      detail: says(""),
    }),
  },
  {
    cap: "A course edition",
    useBox: (): FactBox<null> => ({
      kind: "fact", id: "course", job: "Name the course edition", label: "TDAY-101", owner: imam, fold: "settled", read: ready(null), tone: { identity: COURSE_HUE[3] },
      prose: says("Teaching Day: Ethics in Practice"), detail: says("Next: Session 5, Wed, Oct 21"),
      act: { verb: "Open course", does: "exit", to: back },
    }),
  },
  {
    cap: "Fields that each save and say so",
    note: "A teaching profile",
    useBox: function useProfile(): FactBox<null> {
      // A save word is an event: empty until the person saves (OC 5).
      const name = useSavedField("Zahra Ali", "Zahra Ali", { status: "idle" });
      const hours = useSavedField("Tuesdays after class", "Room 204, Tuesdays after class", { status: "failed", sentence: "The connection dropped. Your text is kept here.", retry: () => undefined });
      const languages = useSavedField("Arabic", "Arabic and English", { status: "unknown", check: () => undefined });
      return {
        kind: "fact", id: "profile", job: "Keep the teaching profile current", label: "Teaching profile", owner: imam, fold: "never", read: ready(null),
        detail: says(""),
        fields: () => [
          { id: "name", label: "Preferred name", render: name.render, save: name.save, note: "Learners in your courses see this." },
          { id: "hours", label: "Office hours", render: hours.render, save: hours.save },
          { id: "languages", label: "Teaching languages", render: languages.render, save: languages.save },
        ],
      };
    },
  },
];

/** A read whose Try again or Refresh reads it again, ready this time, as a server would answer. */
function useRecovering<T>(start: Read<T>, value: T = null as T): Read<T> {
  const [read, setRead] = useState<Read<T>>(start);
  const again = () => setRead(ready(value));
  if (read.status === "failed") return { ...read, retry: again };
  if (read.status === "stale") return { ...read, refresh: again };
  return read;
}

/**
 * A value edited where it's drawn (CASE-026): leaving it or Enter saves only
 * this field, Esc restores the saved value, and a save that didn't arrive keeps
 * the text with Retry.
 */
function useSavedField(saved: string, typed: string, start: FieldSave) {
  const [kept, setKept] = useState(saved);
  const [value, setValue] = useState(typed);
  const [save, setSave] = useState<FieldSave>(start);
  // The text whose save got no answer: only Check settles it, so leaving the field or Enter never sends it again (W1).
  const [unanswered, setUnanswered] = useState(start.status === "unknown" ? typed : null);
  const store = (next: string) => {
    if (next === unanswered) return;
    // Back at the saved value there is nothing to send, and a refusal of another value no longer applies.
    if (next === kept) {
      if (save.status === "refused") setSave({ status: "idle" });
      return;
    }
    setKept(next);
    setUnanswered(null);
    setSave({ status: "saved" });
  };
  // Check reads what the server holds; here the lost save had landed. A newer edit was saved as the person left the field for Check.
  const check = () => {
    setKept(unanswered ?? kept);
    setUnanswered(null);
    setSave({ status: "saved" });
  };
  // Retry is for a save that never arrived; a refused value is fixed in the field.
  const shown: FieldSave = save.status === "failed" ? { ...save, retry: () => store(value) } : save.status === "unknown" ? { status: "unknown", check } : save;
  const render = (labelledBy: string, aria: FieldAria) => (
    <Input
      aria-labelledby={labelledBy}
      {...aria}
      className="max-w-sm"
      value={value}
      onChange={(event) => {
        setValue(event.target.value);
        // A new edit isn't saved yet: the save word is the last event's, never a resting badge (OC 5).
        if (save.status === "saved") setSave({ status: "idle" });
      }}
      onBlur={() => store(value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") store(value);
        if (event.key === "Escape") {
          // Until Check answers, the text last sent is the one to go back to.
          setValue(unanswered ?? kept);
          if (unanswered === null) setSave({ status: "idle" });
        }
      }}
    />
  );
  return { render, save: shown };
}

/* ---------- List ---------- */

/** A learner's row; one that reads like its neighbours carries no mark (K3). */
type Learner = { name: string; state: BoxState | null; facts: string };
const MARYAM: Learner = { name: "Maryam Teaching Day", state: { class: "waiting", word: "To review" }, facts: "Attempt 1 · handed in Wed, Oct 21, 12:38 AM · 47 words" };
const ZAYNAB: Learner = { name: "Zaynab al-Amīnī", state: null, facts: "No attempt yet" };
const learnerRow = { key: (row: Learner) => row.name, title: (row: Learner) => row.name, state: (row: Learner) => row.state, facts: (row: Learner) => row.facts };
const review = { label: "Review", owner: imam, fold: "choice" } as const;

type Item = { title: string; state: BoxState; facts: string };
const ITEMS: Item[] = [
  { title: "Two rulings", state: { class: "waiting", word: "To settle in IMAM" }, facts: "Material · posted Tue, Oct 20" },
  { title: "Compare two rulings", state: { class: "waiting", word: "To settle in IMAM" }, facts: "Assignment · due Fri, Oct 23, 8:00 PM" },
  { title: "Session 9: Live seminar", state: { class: "waiting", word: "To settle in IMAM" }, facts: "Announcement · posted Tue, Oct 20" },
  { title: "Purity and prayer", state: { class: "waiting", word: "To settle in IMAM" }, facts: "Topic" },
  { title: "1 item", state: { class: "reading", word: "Not read" }, facts: "" },
];
const itemRow = { key: (row: Item) => row.title, title: (row: Item) => row.title, state: (row: Item) => row.state, facts: (row: Item) => row.facts };
const KINDS = [{ value: "all", label: "All kinds" }, ...["Material", "Assignment", "Announcement", "Topic"].map((kind) => ({ value: kind, label: `${kind}s` }))];

/** A session a teacher meets, under its day's line; only one that meets now can be joined. */
type SessionDay = { title: string; day: string; time: string; live?: true };
const BY_DAY: SessionDay[] = [
  { title: "Aqaid 201 · Session 9", day: "Today", time: "6:00 PM to 7:30 PM", live: true },
  { title: "Fiqh 101 · Session 4", day: "Today", time: "8:00 PM to 9:00 PM" },
  { title: "Akhlaq 110 · Session 2", day: "Tue, Oct 27", time: "6:00 PM to 7:00 PM" },
  { title: "Tafsir 205 · Session 7", day: "Wed, Oct 28", time: "7:00 PM to 8:30 PM" },
];

const list: Spec[] = [
  {
    cap: "Waiting on you: the page's next step",
    useBox: (): ListBox<null, Learner> => ({
      kind: "list", id: "review", job: "Judge what learners handed in", ...review, read: ready(null),
      state: is({ class: "waiting", word: "1 to review" }), detail: says("1 of 2 handed in"),
      act: { verb: "Review Maryam's attempt", does: "open" }, candidate: rank1,
      rows: () => [MARYAM, ZAYNAB], row: learnerRow,
    }),
  },
  {
    cap: "Not refreshed: never shown as current",
    useBox: function useStaleList(): ListBox<null, Learner> {
      const read = useRecovering<null>({ status: "stale", last: null, readAt: "2026-10-21T04:37:00Z", sentence: "Couldn't refresh. Later hand-ins aren't shown.", refresh: noop });
      return {
        kind: "list", id: "review", job: "Judge what learners handed in", ...review, read,
        state: is({ class: "quiet", word: "Nothing handed in" }), detail: says("Compare two rulings"),
        rows: () => [], row: learnerRow,
      };
    },
  },
  {
    cap: "Another system's records, read-only",
    useBox: (): ListBox<null, Item> => ({
      kind: "list", id: "classroom", job: "Show what Classroom posted for this course", label: "Posted coursework", owner: classroom, fold: "choice", read: ready(null),
      state: is({ class: "quiet", word: "5 items" }), detail: says("4 to settle in IMAM · 1 not read"),
      exits: [{ label: "Show", reopens: true }],
      rows: () => ITEMS, row: itemRow,
    }),
  },
  {
    cap: "A filter that couldn't read its choices says so, with Try again",
    open: true,
    useBox: function useFailedFilter(): ListBox<null, Item> {
      // Until the kinds are read, the filter offers only "All kinds", and says why (MA-3).
      const [read, setRead] = useState(false);
      const [kind, setKind] = useState("all");
      return {
        kind: "list", id: "classroom", job: "Show what Classroom posted for this course", label: "Posted coursework", owner: classroom, fold: "choice", read: ready(null),
        state: is({ class: "quiet", word: "5 items" }), detail: says("4 to settle in IMAM · 1 not read"),
        find: {
          placeholder: "Find posted coursework",
          facets: [{
            label: "Kind", value: kind, options: read ? KINDS : KINDS.slice(0, 1), onChange: setKind,
            read: read ? undefined : { status: "failed", sentence: "Couldn't read the kinds.", retry: () => setRead(true) },
          }],
        },
        rows: () => ITEMS.filter((item) => kind === "all" || item.facts.startsWith(kind)), row: itemRow,
      };
    },
  },
  {
    cap: "Settled, from Classroom: one line that still says whose it is and when it was read",
    // Full width, so a wide page holds the line whole and a phone moves the owner under the label.
    full: true,
    useBox: (): ListBox<null, Item> => ({
      kind: "list", id: "classroom", job: "Show what Classroom posted for this course", label: "Posted coursework", owner: classroom, fold: "settled", read: ready(null),
      state: is({ class: "done", word: "All settled in IMAM" }), detail: says("5 items"),
      exits: [{ label: "Show", reopens: true }],
      // Every row is settled, so none differs and none is marked (K3).
      rows: () => ITEMS, row: { ...itemRow, state: () => null },
    }),
  },
  {
    cap: "Open: find and filter are the List's own tools",
    open: true,
    useBox: function useFilteredList(): ListBox<null, Item> {
      const [query, setQuery] = useState("");
      const [kind, setKind] = useState("all");
      const shown = ITEMS.filter((item) => (kind === "all" || item.facts.startsWith(kind)) && item.title.toLowerCase().includes(query.toLowerCase()));
      return {
        kind: "list", id: "classroom", job: "Show what Classroom posted for this course", label: "Posted coursework", owner: classroom, fold: "choice", read: ready(null),
        state: is({ class: "quiet", word: "5 items" }), detail: says("4 to settle in IMAM · 1 not read"),
        find: { placeholder: "Find posted coursework", facets: [{ label: "Kind", value: kind, options: KINDS, onChange: setKind }], onQueryChange: setQuery, hidden: ITEMS.length - shown.length },
        // Its way to another IMAM page sits in the open head, as page navigation (SD-7).
        exits: [{ label: "Bring in from Classroom", to: back }],
        rows: () => shown, row: itemRow,
      };
    },
  },
  {
    cap: "Open: short names beside a long mark and its act",
    open: true,
    // A register's closeout: the mark and act take at most half the row, so a name never breaks inside itself.
    useBox: (): ListBox<null, Learner> => ({
      kind: "list", id: "closeout", job: "Keep proposals apart from certified outcomes", label: "Final results", owner: imam, fold: "never", read: ready(null),
      state: is({ class: "quiet", word: "No proposals awaiting certification" }), detail: says(""),
      rows: () => ["Maryam", "Zaynab"].map((name) => ({ name, state: { class: "quiet", word: "Final grade not submitted" }, facts: "" })),
      row: {
        ...learnerRow,
        act: () => ({ verb: "Read outcome", does: "open" }),
        detail: (row) => <p>No final grade has been submitted for {row.name}.</p>,
      },
    }),
  },
  {
    cap: "Open: one List by day, with a quiet line where the day changes",
    open: true,
    // The lines come from each row's own day, in the read's order; the head keeps the one count.
    useBox: (): ListBox<null, SessionDay> => ({
      kind: "list", id: "sessions", job: "Show the sessions you teach this week", label: "My sessions", owner: imam, fold: "never", read: ready(null),
      state: is({ class: "quiet", word: "4 sessions this week" }), detail: says(""),
      rows: () => BY_DAY,
      row: {
        key: (row) => row.title, title: (row) => row.title, facts: (row) => row.time, group: (row) => row.day,
        act: (row) => (row.live ? { verb: "Join in Zoom", does: "leave", providerUrl: "https://zoom.us/j/1" } : undefined),
      },
    }),
  },
  {
    cap: "Done: one line",
    useBox: (): ListBox<null, Learner> => ({
      kind: "list", id: "review", job: "Judge what learners handed in", ...review, read: ready(null),
      state: is({ class: "done", word: "Nothing to review" }), detail: says("Maryam's attempt 1 returned for revision · 1 of 2 handed in"),
      exits: [{ label: "Show", reopens: true }],
      rows: () => [{ ...MARYAM, state: { class: "done", word: "Returned" } }, ZAYNAB], row: learnerRow,
    }),
  },
  {
    cap: "Nothing handed in yet",
    useBox: (): ListBox<null, Learner> => ({
      kind: "list", id: "review", job: "Judge what learners handed in", ...review,
      read: { status: "empty", sentence: "Nothing handed in", sub: "Counsel and evidence" },
      state: is({ class: "quiet", word: "Nothing handed in" }), detail: says(""),
      rows: () => [], row: learnerRow,
    }),
  },
  {
    cap: "Couldn't read",
    useBox: function useFailedList(): ListBox<null, Learner> {
      const read = useRecovering<null>({ status: "failed", sentence: "Couldn't read hand-ins", detail: "Nothing handed in is lost. Try again in a moment.", retry: noop });
      return {
        kind: "list", id: "review", job: "Judge what learners handed in", ...review, read,
        state: is(read.status === "ready" ? { class: "waiting", word: "1 to review" } : { class: "quiet", word: "Nothing handed in" }), detail: says("1 of 2 handed in"),
        rows: () => [MARYAM, ZAYNAB], row: learnerRow,
      };
    },
  },
  {
    cap: "Open: a row opens in place for its judgement, with its own selection and quick score",
    full: true,
    open: true,
    useBox: function useOpenReview(): ListBox<null, Learner> {
      const [comment, setComment] = useState("");
      const [release, setRelease] = useState(true);
      const [picked, setPicked] = useState<string[]>([]);
      const [score, setScore] = useState("8");
      const [result, settle] = useResult();
      const done = result.state === "confirmed";
      return {
        kind: "list", id: "review", job: "Judge what learners handed in", ...review, read: ready(null),
        state: is(done ? { class: "done", word: "Nothing to review" } : { class: "waiting", word: "1 to review" }),
        detail: says("Counsel and evidence · 1 of 2 handed in"),
        candidate: rank1, draft: !done && comment.trim() !== "",
        rows: () => [done ? { ...MARYAM, state: { class: "done", word: "Returned" } } : MARYAM, ZAYNAB],
        row: {
          ...learnerRow,
          act: (row) => (row === ZAYNAB ? undefined : { verb: "Review", does: "open" }),
          startsOpen: (row) => row !== ZAYNAB,
          // A narrow box draws them under the row's title, so the title keeps its width.
          controls: (row) => (
            <>
              <label className="flex min-h-11 items-center gap-2 text-ui">
                <input type="checkbox" aria-label={`Select ${row.name}`} checked={picked.includes(row.name)} onChange={(event) => setPicked((was) => (event.target.checked ? [...was, row.name] : was.filter((name) => name !== row.name)))} />
                Select
              </label>
              {row === MARYAM && !done && (
                <label className="flex items-center gap-2 text-ui">
                  Score
                  <Input className="w-20" inputMode="decimal" aria-label={`Quick score for ${row.name}`} value={score} onChange={(event) => setScore(event.target.value)} />
                  <span className="text-meta">of 20</span>
                </label>
              )}
            </>
          ),
          detail: (row) => (row === ZAYNAB || done ? undefined : (
            <p className="border-s-[3px] border-border-strong ps-3.5 text-foreground">
              My provisional counsel is to disclose the conflict of interest before accepting the gift. The evidence is that disclosure preserves the affected person&apos;s informed choice.
            </p>
          )),
          task: (row) => (row === ZAYNAB ? undefined : {
            render: () => (done ? null : (
              <label className="grid gap-1.5">
                <span className="text-meta font-semibold text-foreground-soft">Comment to Maryam</span>
                <Textarea rows={3} value={comment} onChange={(event) => setComment(event.target.value)} />
              </label>
            )),
            commit: {
              verb: "Return for revision", cap: "When you record your judgement",
              effects: () => [
                { id: "record", sentence: "Record attempt 1 as returned for revision" },
                { id: "release", sentence: "Release it to Maryam now, so a second attempt can be handed in", optional: { checked: release, toggle: () => setRelease((on) => !on) } },
              ],
              blockers: () => (comment.trim() ? [] : [{ kind: "missing", part: "comment", sentence: "Write a comment to Maryam first." }]),
              run: settle(confirmed(undefined, ["Attempt 1 recorded as returned for revision", release ? "Released to Maryam with your comment" : "Kept private until you release it to Maryam"])),
            },
            result,
          }),
        },
      };
    },
  },
  {
    cap: "Open: scores in a table, each saved in its cell, and a long override opened in its row",
    full: true,
    open: true,
    useBox: function useScores(): ListBox<null, string> {
      const cells: Record<string, ReturnType<typeof useSavedField>> = {
        "Maryam Teaching Day:reflection": useSavedField("82", "82", { status: "saved" }),
        "Maryam Teaching Day:counsel": useSavedField("88", "88", { status: "idle" }),
        "Zaynab al-Amīnī:reflection": useSavedField("", "", { status: "idle" }),
        "Zaynab al-Amīnī:counsel": useSavedField("74", "740", { status: "refused", sentence: "Scores run from 0 to 100." }),
        "Baqir Salih:reflection": useSavedField("91", "91", { status: "idle" }),
        "Baqir Salih:counsel": useSavedField("85", "85", { status: "idle" }),
      };
      const [override, setOverride] = useState("");
      const [reason, setReason] = useState("");
      const [result, settle] = useResult();
      const percent = override.trim() === "" ? null : Number(override);
      const resultBlocker = override.trim() === ""
        ? "Enter the new course result first."
        : percent === null || Number.isNaN(percent) || percent < 0 || percent > 100 ? "A course result runs from 0 to 100." : null;
      // A confirmed override is Maryam's course result from then on.
      const maryam = result.state === "confirmed" ? `${percent}%` : "86%";
      const course: Record<string, string> = { "Maryam Teaching Day": maryam, "Zaynab al-Amīnī": "Needs Case reflection", "Baqir Salih": "87%" };
      return {
        kind: "list", id: "scores", job: "Record each learner's score on each assessment", label: "Scores by assessment", owner: imam, fold: "never", read: ready(null),
        state: is({ class: "failed", word: "1 score not saved" }), detail: says("3 learners · 2 assessments"), draft: override.trim() !== "" || reason.trim() !== "",
        candidate: rank1,
        table: {
          title: "Learner",
          columns: [{ id: "reflection", label: "Case reflection", sub: "40%" }, { id: "counsel", label: "Final counsel", sub: "60%" }, { id: "course", label: "Course %" }],
          note: "Learners see their working result as each score saves.",
        },
        rows: () => ["Maryam Teaching Day", "Zaynab al-Amīnī", "Baqir Salih"],
        row: {
          key: (row) => row, title: (row) => row,
          cells: (row) => [
            { id: "reflection", ...cells[`${row}:reflection`] },
            { id: "counsel", ...cells[`${row}:counsel`] },
            { id: "course", read: course[row] },
          ],
          act: (row) => (row === "Maryam Teaching Day" ? { verb: "Override", does: "open" } : undefined),
          // The cells give the scores and the heads their weights: the row adds only when each was recorded.
          detail: () => <p className="m-0 text-ui text-foreground-soft">Case reflection recorded Tue, Oct 20 · Final counsel recorded Wed, Oct 21</p>,
          task: () => ({
            render: () => (result.state === "confirmed" ? null : (
              <div className="grid gap-4">
                <label className="grid gap-1.5">
                  <span className="text-meta font-semibold text-foreground-soft">New course result (%)</span>
                  <Input className="max-w-[8rem]" inputMode="decimal" value={override} onChange={(event) => setOverride(event.target.value)} />
                </label>
                <label className="grid gap-1.5">
                  <span className="text-meta font-semibold text-foreground-soft">Why the course result changes</span>
                  <Textarea className="min-h-[36rem]" value={reason} onChange={(event) => setReason(event.target.value)} />
                </label>
              </div>
            )),
            commit: {
              verb: "Save override", cap: "When you save the override",
              effects: () => [{ id: "result", sentence: resultBlocker ? "Set Maryam's course result, with your reason" : `Set Maryam's course result to ${percent}%, with your reason` }],
              blockers: () => [
                ...(resultBlocker ? [{ kind: "missing" as const, part: "result", sentence: resultBlocker }] : []),
                ...(reason.trim() ? [] : [{ kind: "missing" as const, part: "reason", sentence: "Write why the result changes first." }]),
              ],
              run: settle(confirmed(undefined, [`Maryam's course result is ${percent}%, with your reason`])),
            },
            result,
          }),
        },
      };
    },
  },
  {
    cap: "On a Home: the row the server ranks offers its act, which leads; a name opens its course, beside its mark",
    note: "Every other row act stays quiet.",
    useBox: (): ListBox<null, Meeting> => ({
      kind: "list", id: "week", job: "This week's sessions across your courses", label: "This week", labelTo: "/courses", owner: imam, fold: "never", read: ready(null),
      state: is({ class: "quiet", word: "3 sessions" }), detail: says("3 sessions"),
      candidate: rank1, offers: "aqaid-9",
      rows: () => MEETINGS,
      row: {
        key: (m) => m.id, title: (m) => m.course, course: (m) => m.code, nav: (m) => `/courses/${m.course.toLowerCase().replace(/\s+/g, "-")}`, facts: (m) => m.when,
        act: (m) => ({ verb: m.live ? "Join in Zoom" : "Open in Classroom", does: "leave", providerUrl: m.live ? "https://zoom.us/j/1" : "https://classroom.google.com/" }),
      },
    }),
  },
];

type Meeting = { id: string; course: string; code: string; when: string; live?: true };
const MEETINGS: Meeting[] = [
  { id: "fiqh-4", course: "Fiqh 101", code: "FIQH-101", when: "Session 4 · Tue 7:00 PM" },
  { id: "aqaid-9", course: "Aqaid 201", code: "AQAID-201", when: "Session 9 · now", live: true },
  { id: "akhlaq-2", course: "Akhlaq 110", code: "AKHLAQ-110", when: "Session 2 · Thu 6:30 PM" },
];

/* ---------- Task ---------- */

const PLAN = "Read the two rulings and explain the evidence supporting each.";
const prepareParts = (plan: Part["render"]): Part[] => [
  { id: "before", label: "Before the session", state: { class: "draft", word: "Changed" }, owner: imam, fold: "choice", draft: "server", render: plan, summary: plan ? undefined : PLAN },
  { id: "reading", label: "Reading", state: { class: "draft", word: "New" }, owner: imam, fold: "choice", draft: "server", summary: "Two rulings · link" },
  { id: "task", label: "Task", state: { class: "draft", word: "New" }, owner: imam, fold: "choice", draft: "server", summary: "Compare two rulings · structured writing · completion · due Fri, Oct 23, 8:00 PM" },
];
const publishEffects = (): Effect[] => [
  { id: "before", sentence: "Students read the new text" },
  { id: "reading", sentence: "Publish the reading" },
  { id: "task", sentence: "Publish the task to 9 learners" },
];
const twoLearners = ["Maryam Teaching Day", "Zaynab al-Amīnī"];
const close = { label: "Close", owner: imam, fold: "settled" } as const;

const task: Spec[] = [
  {
    cap: "Waiting on you: the page's next step",
    useBox: (): TaskBox<null> => ({
      kind: "task", id: "close", job: "Close the session", ...close, read: ready(null),
      state: is({ class: "waiting", word: "Roll not taken" }), detail: says("Recap and next steps not written"),
      act: { verb: "Take roll", does: "open" }, candidate: rank1,
      parts: [{ id: "roll", label: "Roll", state: { class: "waiting", word: "0 of 2 marked" }, markedByBox: true, owner: imam, fold: "never", draft: "browser", summary: twoLearners.join(", ") }],
      commit: {
        verb: "Close session", cap: "When you close Session 5", run: async () => none,
        effects: () => [{ id: "roll", sentence: "Record the roll" }, { id: "held", sentence: "Mark Session 5 held" }],
        blockers: () => [{ kind: "missing", part: "roll", sentence: "Mark everyone first." }],
      },
      result: none, draft: false,
    }),
  },
  {
    cap: "Changed, not published: still says what students read",
    useBox: (): TaskBox<null> => ({
      kind: "task", id: "prepare", job: "Publish what learners read before the session", label: "Prepare", owner: imam, fold: "settled", read: ready(null),
      state: is({ class: "draft", word: "Changed, not published" }), detail: says("Before the session, a reading and a task"), sub: says("Students still read the earlier text"),
      act: { verb: "Show 3 changes", does: "open" },
      parts: prepareParts(undefined),
      commit: { verb: "Publish 3 items", cap: "When you publish", effects: publishEffects, blockers: () => [], run: async () => none },
      result: none, draft: true,
    }),
  },
  {
    cap: "Kept in this tab: hide it or reload, and the note is still there",
    useBox: function useKeptNote(): TaskBox<null> {
      const [saved, setSaved] = useState({ text: "", version: "1" });
      const note = useTabDraft({ box: "note", record: "session-9", version: saved.version, initial: saved.text });
      const [result, setResult] = useState<Result>(none);
      return {
        // Until the session says who is signed in, the note's draft isn't known, so the box is still reading.
        kind: "task", id: "note", job: "Leave a note for the next session", label: "Note for Session 10", owner: imam, fold: "choice", read: note.settled ? ready(null) : { status: "loading" },
        state: is(note.kept ? { class: "draft", word: "Not saved" } : saved.text ? { class: "done", word: "Note saved" } : { class: "quiet", word: "No note" }), detail: says("Session 9"),
        act: { verb: note.kept ? "Finish the note" : saved.text ? "Change the note" : "Write a note", does: "open" },
        parts: [{
          // The head says whether the note is saved, so the part draws no mark of its own (CASE-030).
          id: "note", label: "Note", owner: imam, fold: "never", draft: "browser", keep: note, markedByBox: true,
          state: note.kept ? { class: "draft", word: "Not saved" } : { class: "quiet", word: saved.text ? "Saved" : "Empty" },
          render: () => <Textarea aria-label="Note for Session 10" value={note.value} onChange={(event) => note.set(event.target.value)} />,
        }],
        commit: {
          verb: "Save note", cap: "When you save", effects: () => [{ id: "save", sentence: "Session 10 shows the note" }],
          blockers: () => (note.kept ? [] : [{ kind: "missing", part: "note", sentence: "Write the note first." }]),
          run: async () => {
            const next = confirmed(undefined, ["Session 10 shows the note"]);
            setSaved({ text: note.value, version: String(Number(saved.version) + 1) });
            setResult(next);
            return next;
          },
        },
        result, draft: false,
      };
    },
  },
  {
    cap: "Can't hand in yet: says why in place",
    useBox: (): TaskBox<null> => ({
      kind: "task", id: "hand-in", job: "Hand in a revised attempt", label: "Hand in", owner: imam, fold: "settled", read: ready(null),
      state: is({ class: "waiting", word: "Returned for revision" }), detail: says("Compare two rulings"),
      act: { verb: "Hand in attempt 2", does: "open", opens: "writing" },
      candidate: { eligible: true, blocked: { kind: "unchanged", part: "writing", attempt: 1 }, rank: 1 },
      parts: [{ id: "writing", label: "Your writing", state: { class: "draft", word: "Draft" }, owner: imam, fold: "never", draft: "server" }],
      commit: {
        verb: "Hand in attempt 2", cap: "When you hand it in", run: async () => none,
        effects: () => [{ id: "hand-in", sentence: "Your teacher can review it; attempt 1 stays as it was" }],
        blockers: () => [], revises: { attempt: 1, part: "writing", unchanged: () => true },
      },
      result: none, draft: true,
    }),
  },
  {
    cap: "Done: one line with its facts",
    useBox: (): TaskBox<null> => ({
      kind: "task", id: "close", job: "Close the session", ...close, read: ready(null),
      state: is({ class: "done", word: "Held" }), detail: says("9 present · recap published"),
      exits: [{ label: "Update roll", reopens: true }],
      parts: [{ id: "roll", label: "Roll", state: { class: "done", word: "Recorded" }, owner: imam, fold: "choice", draft: "browser", summary: "All 9 learners present." }],
      commit: {
        verb: "Save roll changes", cap: "When you update the roll for Session 9", run: async () => none,
        effects: () => [{ id: "roll", sentence: "Record the roll: 9 present, 0 absent" }],
        blockers: () => [{ kind: "unchanged", part: "roll" }],
      },
      result: none, draft: false,
    }),
  },
  {
    cap: "Open: each change, then one Publish that says what it does",
    full: true,
    open: true,
    useBox: function usePrepare(): TaskBox<null> {
      const [plan, setPlan] = useState(PLAN);
      const [result, settle] = useResult();
      const done = result.state === "confirmed";
      return {
        kind: "task", id: "prepare", job: "Publish what learners read before the session", label: "Prepare", owner: imam, fold: "settled", read: ready(null),
        state: is(done ? { class: "done", word: "Published" } : { class: "draft", word: "Changed, not published" }),
        detail: says(done ? "Students read the new text" : "Students still read the earlier text"),
        candidate: rank1,
        parts: prepareParts(() => (
          <label className="grid gap-1.5">
            <span className="text-meta font-semibold text-foreground-soft">Instructions</span>
            <Textarea rows={2} value={plan} onChange={(event) => setPlan(event.target.value)} />
          </label>
        )),
        commit: {
          verb: "Publish 3 items", cap: "When you publish", note: "Recap and next steps reach students once the session is held.",
          effects: publishEffects, blockers: () => [],
          run: settle(confirmed(undefined, ["Students read the new instructions", "Published the reading: Two rulings", "Published the task to 9 learners, due Fri, Oct 23, 8:00 PM"])),
        },
        result, draft: !done,
      };
    },
  },
  {
    cap: "Someone else saved: says what changed, and waits for your choice",
    full: true,
    open: true,
    useBox: function useConflict(): TaskBox<null> {
      const [late, setLate] = useState<boolean | null>(null);
      const [result, settle] = useResult();
      const roll = late ? "1 present, 1 late, 0 absent" : "2 present, 0 absent";
      const done = result.state === "confirmed";
      return {
        kind: "task", id: "close", job: "Close the session", ...close, read: ready(null),
        state: is(done ? { class: "done", word: "Held" } : late === null ? { class: "failed", word: "Roll changed since you opened it" } : { class: "draft", word: "Roll marked, not recorded" }),
        detail: says("Session 5 · your marks are kept"),
        parts: [{
          id: "roll", label: "Roll", owner: imam, fold: "never", draft: "browser", markedByBox: true,
          state: late === null ? { class: "failed", word: "Changed since you opened it" } : { class: "draft", word: "Not recorded" },
          summary: late === null ? undefined : twoLearners.map((name) => `${name}: ${late && name === "Zaynab al-Amīnī" ? "Late" : "Present"}`).join(" · "),
          conflict: late === null ? {
            sentence: "Someone recorded the roll at 12:44 AM. Your draft says Present for each learner.",
            facts: [{ label: "Mark for Zaynab al-Amīnī", theirs: "Late" }],
            keepMine: { label: "Keep my roll", run: () => setLate(false) },
            loadNewer: { label: "Load newer roll", run: () => setLate(true) },
          } : undefined,
        }],
        commit: {
          verb: "Close session", cap: "When you close Session 5",
          effects: () => [{ id: "roll", sentence: `Record the roll: ${roll}` }, { id: "held", sentence: "Mark Session 5 held" }],
          blockers: () => (late === null ? [{ kind: "conflict", part: "roll", sentence: "Choose which roll to keep." }] : []),
          run: settle(confirmed(undefined, [`Roll recorded: ${roll}`])),
        },
        result, draft: !done,
      };
    },
  },
];

/* ---------- Outcomes: the four write results, each with the one act that makes sense ---------- */

const closedNine = confirmed(undefined, ["Roll recorded: 9 present, 0 absent", "Recap and next steps published to 9 learners"], ["Still open: add a recording link once Zoom has one"]);
const closeNine = (result: Result, publish = true, toggle: () => void = noop) => ({
  verb: "Close session", cap: "When you close Session 9",
  effects: (): Effect[] => [
    { id: "roll", sentence: "Record the roll: 9 present, 0 absent" },
    { id: "held", sentence: "Mark Session 9 held" },
    { id: "publish", sentence: "Publish the recap and next steps to 9 learners", optional: { checked: publish, toggle } },
  ],
  blockers: () => [],
  run: async () => result,
});
/* In a partial result the foot says the roll went, so the part draws no mark (CASE-030). */
const rollNine: Part = { id: "roll", label: "Roll", state: { class: "done", word: "Recorded" }, markedByBox: true, owner: imam, fold: "choice", draft: "none", summary: "Nothing here is sent again." };

const announced = confirmed(undefined, ["Posted to the course page", "Email accepted for 24 learners"]);

/** An announcement posted and emailed: the post went, and the email's outcome is the part a result holds. */
const announcement = (shown: Result, open: BoxState): TaskBox<null> => {
  const done = shown.state === "confirmed";
  return {
    kind: "task", id: "announce", job: "Post an announcement", label: "Announcement", owner: imam, fold: "settled", read: ready(null),
    state: is(done ? { class: "done", word: "Posted" } : open), detail: says("Session 9 reading · 24 learners"),
    candidate: done ? undefined : rank1,
    parts: [],
    commit: {
      verb: "Post", cap: "When you post",
      effects: () => [{ id: "post", sentence: "Post to the course page" }, { id: "email", sentence: "Email 24 learners" }],
      blockers: () => [], run: async () => announced,
    },
    result: shown, draft: !done,
  };
};

const outcomes: Spec[] = [
  {
    cap: "Done: what happened, and what is still open",
    open: true,
    useBox: (): TaskBox<null> => ({
      kind: "task", id: "close", job: "Close the session", ...close, read: ready(null),
      state: is({ class: "done", word: "Held" }), detail: says("Session 9 · Wed, Oct 21"),
      exits: [{ label: "Update roll", reopens: true }],
      parts: [rollNine], commit: closeNine(closedNine), result: closedNine, draft: false,
    }),
  },
  {
    cap: "Partly done: names what failed, and retries only that",
    open: true,
    useBox: function usePartial(): TaskBox<null> {
      const [result, settle] = useResult();
      const shown: Result = result.state === "none" ? {
        state: "partial", cap: "Session 9 held", at: AT,
        went: [{ ids: ["roll-9"], sentence: "Roll recorded: 9 present, 0 absent" }],
        failed: [{ ids: ["recap-9", "next-9"], sentence: "Recap and next steps: the server was busy. They are saved." }],
        requestId: "specimen-partial",
        retry: { verb: "Publish the recap", note: "Sends only the recap and next steps.", run: settle(closedNine) },
      } : result;
      const done = shown.state === "confirmed";
      return {
        kind: "task", id: "close", job: "Close the session", ...close, read: ready(null),
        state: is(done ? { class: "done", word: "Held" } : { class: "failed", word: "Recap not published" }),
        detail: says(done ? "Session 9 · Wed, Oct 21" : "Session 9 held · 9 present"),
        candidate: done ? undefined : rank1,
        parts: [rollNine], commit: closeNine(closedNine), result: shown, draft: !done,
      };
    },
  },
  {
    cap: "Not done: keeps your work and says why",
    open: true,
    useBox: function useRefused(): TaskBox<null> {
      const [publish, setPublish] = useState(true);
      const [result, setResult] = useState<Result>({
        state: "refused", cap: "Not closed.", draft: "kept",
        sentence: `Your sign-in ended before the close reached IMAM. Sign in again, then close.`,
      });
      const done = result.state === "confirmed";
      const commit = closeNine(closedNine, publish, () => setPublish((on) => !on));
      return {
        kind: "task", id: "close", job: "Close the session", ...close, read: ready(null),
        state: is(done ? { class: "done", word: "Held" } : { class: "draft", word: "Roll marked, not recorded" }),
        detail: says(done ? "Session 9 · Wed, Oct 21" : "Session 9 · held once you close"),
        candidate: done ? undefined : rank1,
        parts: [], result, draft: !done,
        commit: { ...commit, run: async () => (setResult(closedNine), closedNine) },
      };
    },
  },
  {
    cap: "Partly done, part not known: checks that part, never sends it again",
    open: true,
    useBox: function usePartialCheck(): TaskBox<null> {
      const [result, settle] = useResult();
      const shown: Result = result.state === "none" ? {
        state: "partial", at: AT, requestId: "specimen-announce",
        went: [{ ids: ["post"], sentence: "Posted to the course page" }],
        failed: [],
        unsure: [{ ids: ["email"], sentence: "Email to 24 learners: the provider didn't answer, so it may have gone." }],
        check: { verb: "Check the email", note: "Checking never sends it again.", run: settle(announced) },
      } : result;
      return announcement(shown, { class: "reading", word: "Email not confirmed" });
    },
  },
  {
    cap: "Partly done, some failed and some not known: retries the failed, checks the rest",
    open: true,
    useBox: function usePartialBoth(): TaskBox<null> {
      // Only Retry sends to the 3 refused, and only Check settles the 2 not known, in either order.
      const [done, setDone] = useState({ resent: false, checked: false });
      const resultOf = (resent: boolean, checked: boolean): Result => {
        if (resent && checked) return announced;
        const went = [{ ids: ["post"], sentence: "Posted to the course page" }, { ids: ["email"], sentence: `Email accepted for ${19 + (resent ? 3 : 0) + (checked ? 2 : 0)} learners` }];
        const failed = resent ? [] : [{ ids: ["email-3"], sentence: "Email to 3 learners: the provider refused it." }];
        const base = { state: "partial" as const, at: AT, requestId: "specimen-announce", went, failed };
        const retry = { verb: "Send to the 3", note: "Sends only to the 3 it failed for.", run: async () => (setDone({ resent: true, checked }), resultOf(true, checked)) };
        if (checked) return { ...base, retry };
        const unsure: [ResultLine] = [{ ids: ["email-2"], sentence: "Email to 2 learners: the provider didn't answer, so it may have gone." }];
        const check = { verb: "Check the 2", note: "Checking never sends it again.", run: async () => (setDone({ resent, checked: true }), resultOf(resent, true)) };
        return resent ? { ...base, unsure, check } : { ...base, unsure, check, retry };
      };
      const shown = resultOf(done.resent, done.checked);
      const failed = shown.state === "partial" && shown.failed.length > 0;
      return announcement(shown, failed ? { class: "failed", word: "Email not sent to everyone" } : { class: "reading", word: "Email not confirmed" });
    },
  },
  {
    cap: "Not confirmed: offers a check, never a second close",
    open: true,
    useBox: function useUnknown(): TaskBox<null> {
      const [result, settle] = useResult();
      const shown: Result = result.state === "none" ? {
        state: "unknown", cap: "Close sent", requestId: "specimen-close",
        sentence: `The connection dropped before IMAM answered, so Session 9 may already be held.`,
        check: { verb: "Check Close result", note: "Closing again could repeat it.", run: settle({ ...closedNine, cap: "Checked: the first close went through" }) },
      } : result;
      const done = shown.state === "confirmed";
      return {
        kind: "task", id: "close", job: "Close the session", ...close, read: ready(null),
        state: is(done ? { class: "done", word: "Held" } : { class: "reading", word: "Close not confirmed" }),
        detail: says(done ? "Session 9 · Wed, Oct 21" : "Session 9 · sent 12:44 AM"),
        candidate: done ? undefined : rank1,
        parts: [], commit: closeNine(closedNine), result: shown, draft: !done,
      };
    },
  },
];

/* ---------- Composite: one task across several owners and parts, one review, one commit ---------- */

const INTAKE: { title: string; state: BoxState; facts: string }[] = [
  { title: "Baqir Salih", state: { class: "done", word: "Matched" }, facts: "IMAM membership on file" },
  { title: "2 learners", state: { class: "waiting", word: "No IMAM membership" }, facts: "Invite, or leave out of this intake" },
  { title: "6 more learners", state: { class: "done", word: "Matched" }, facts: "IMAM membership on file" },
];

const composite: Spec[] = [
  {
    cap: "One change from several sources, confirmed once",
    full: true,
    open: true,
    useBox: (): TaskBox<null> => ({
      kind: "task", id: "intake", job: "Bring a Classroom course into IMAM", label: "Bring in a course", owner: imam, fold: "settled", read: ready(null),
      state: is({ class: "waiting", word: "2 decisions before you confirm" }), detail: says("USUL-210 · Uṣūl al-Fiqh: al-Rasāʾil"),
      candidate: rank1,
      parts: [
        { id: "source", label: "What's there", state: { class: "done", word: "Read" }, owner: classroom, fold: "never", draft: "none", summary: "1 course, 1 teacher, 9 students, as Classroom lists them. Nothing is created until you confirm." },
        { id: "section", label: "Section", state: { class: "done", word: "Matched" }, owner: imam, fold: "never", draft: "none", summary: "USUL-210 · CRN 20620 · Fall 2026" },
        { id: "teacher", label: "Teacher", state: { class: "done", word: "Matched" }, owner: imam, fold: "never", draft: "none", summary: "Sayyid Muḥammad Ṭabāṭabāʾī, by IMAM membership" },
        {
          id: "learners", label: "Learners", state: { class: "waiting", word: "2 to decide" }, markedByBox: true, owner: imam, fold: "never", draft: "browser",
          render: () => (
            <Register rowsOnly template="minmax(0,1fr) auto">
              {INTAKE.map((row) => (
                <RegisterRow key={row.title}>
                  <span className="min-w-0">
                    <span className="font-semibold">{row.title}</span>
                    <span className="block text-meta text-muted-foreground">{row.facts}</span>
                  </span>
                  <StateMark state={row.state} />
                </RegisterRow>
              ))}
            </Register>
          ),
        },
      ],
      commit: {
        verb: "Bring in 7 learners", cap: "When you confirm", run: async () => none,
        effects: () => [
          { id: "link", part: "section", sentence: "Link USUL-210 to the Classroom course it came from" },
          { id: "teacher", part: "teacher", sentence: "Assign the teacher" },
          { id: "enroll", part: "learners", sentence: "Enroll the 7 learners with an IMAM membership" },
        ],
        blockers: () => [{ kind: "missing", part: "learners", sentence: "Decide for each learner without a membership." }],
      },
      result: none, draft: true,
    }),
  },
];

const keyed = (kind: string, specs: Spec[]): Spec[] => specs.map((spec, n) => ({ ...spec, key: `states-${kind}-${n + 1}` }));
const FACT = keyed("fact", fact);
const LIST = keyed("list", list);
const TASK = keyed("task", task);
const OUTCOMES = keyed("outcomes", outcomes);
const COMPOSITE = keyed("composite", composite);

const STATES: Record<Exclude<StatesGroup, "home">, Spec[]> = {
  all: [...FACT, ...LIST, ...TASK, ...OUTCOMES, ...COMPOSITE],
  fact: FACT,
  list: LIST,
  task: [...TASK, ...COMPOSITE],
  outcomes: OUTCOMES,
};

function Specimen({ spec, page }: { spec: Spec; page: string }) {
  const box = spec.useBox();
  return (
    <figure className={cn("m-0 grid content-start gap-2", spec.full && "md:col-span-2")}>
      <figcaption className="text-meta text-muted-foreground">
        <span className="font-semibold text-foreground-soft">{spec.cap}</span>
        {spec.note && <em className="ms-1.5">({spec.note})</em>}
      </figcaption>
      <Bento id={page} journey={page} boxes={[box]} defaultOpen={spec.open ? [box.id] : []} zone={ZONE} />
    </figure>
  );
}

/** One group of specimens, each its own one-box page. */
/** The six state shapes, each with its word (F2), as the package's States view keys them. */
const LEGEND: BoxState[] = [
  { class: "waiting", word: "waiting on you" },
  { class: "draft", word: "draft" },
  { class: "done", word: "done" },
  { class: "quiet", word: "nothing to do yet" },
  { class: "reading", word: "not known yet" },
  { class: "failed", word: "failed" },
];

export function StatesView({ group }: { group: StatesGroup }) {
  // A Home is one page of several boxes, not one-box specimens (CASE-048).
  if (group === "home") return <HomeView />;
  return (
    <div className="grid gap-x-6 gap-y-8 md:grid-cols-2">
      <ul aria-label="State shapes" className="m-0 flex list-none flex-wrap gap-x-5 gap-y-2 rounded-lg border border-border bg-card px-4 py-3 md:col-span-2">
        {LEGEND.map((state) => (
          <li key={state.class}><StateMark state={state} /></li>
        ))}
      </ul>
      {STATES[group].map((spec) => (
        <Specimen key={spec.key} spec={spec} page={spec.key!} />
      ))}
    </div>
  );
}
